import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    AlignCenter,
    AlignLeft,
    AlignRight,
    ArrowDown,
    ArrowLeft,
    ArrowRight,
    ArrowUp,
    ArrowUpFromLine,
    Ban,
    CaseUpper,
    Italic,
    Minimize2,
    Maximize2,
    MousePointerClick,
    MoveHorizontal,
    WholeWord,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { SegmentedChoice } from '@/components/design-system/common';
import {
    AudioClip,
    ClipFit,
    IN_ONLY_ANIMS,
    ImageLayer,
    LAYER_ANIMS,
    LayerAnim,
    MAX_SPEED,
    MIN_SPEED,
    MOTION_TYPES,
    MainClip,
    MotionType,
    OverlayLayer,
    ShapeLayer,
    TEXT_ONLY_ANIMS,
    TRANSITION_DEFAULT_SECONDS,
    TRANSITION_TYPES,
    TextLayer,
    TransitionType,
    VideoComposition,
    findItem,
    mainTrack,
    maxTransitionDuration,
    setClipSpeed,
    setItemDuration,
    sourceSpan,
    updateItem,
} from './composition';
import { ColorRow, NumberField, PanelSection, SliderRow, TileGrid } from './controls';
import { TransitionTile } from './TransitionTile';
import { EditorSelection } from './Timeline';
import { TEXT_PRESETS, TextPresetId, VideoBrand, buildPresetLayers } from './presets';
import { VIDEO_FONTS, requestFontFamily } from './fonts';
import { CompositionHistory } from './use-composition-history';

interface InspectorProps {
    comp: VideoComposition;
    history: CompositionHistory;
    selection: EditorSelection | null;
    brand: VideoBrand;
}

const NEUTRALS = ['#FFFFFF', '#F4F1EB', '#D6CFC4', '#8A847A', '#3A3733', '#141412', '#000000'];

const MOTION_ICONS: Record<MotionType, React.ReactNode> = {
    none: <Ban />,
    'zoom-in': <Maximize2 />,
    'zoom-out': <Minimize2 />,
    'pan-left': <ArrowLeft />,
    'pan-right': <ArrowRight />,
    'pan-up': <ArrowUp />,
};

const ANIM_ICONS: Partial<Record<LayerAnim, React.ReactNode>> = {
    none: <Ban />,
    rise: <ArrowUp />,
    fall: <ArrowDown />,
    'slide-left': <ArrowLeft />,
    scale: <Maximize2 />,
    'mask-up': <ArrowUpFromLine />,
    'word-stagger': <WholeWord />,
    'tracking-in': <MoveHorizontal />,
};

const CLIP_TITLES: Record<MainClip['kind'], string> = {
    video: 'VIDEO_EDITOR.INSPECTOR.VIDEO_CLIP',
    image: 'VIDEO_EDITOR.INSPECTOR.IMAGE_CLIP',
    color: 'VIDEO_EDITOR.INSPECTOR.COLOR_CLIP',
};

const pct = (v: number) => `${Math.round(v * 100)}%`;
const secs = (v: number) => `${v.toFixed(1)}s`;

export const Inspector: React.FC<InspectorProps> = ({ comp, history, selection, brand }) => {
    const { t } = useTranslation();
    const found = selection ? findItem(comp, selection.id) : null;
    const patch = <T,>(id: string, p: Partial<T>, merge?: string) =>
        history.commit((c) => updateItem(c, id, p as any), merge ? `${id}:${merge}` : undefined);
    const swatches = [...NEUTRALS, ...(brand.palette || [])];

    let body: React.ReactNode;
    if (!selection || !found) {
        body = <CompositionPanel comp={comp} history={history} swatches={swatches} />;
    } else if (selection.kind === 'transition') {
        const clip = found.item as MainClip;
        body = <TransitionPanel comp={comp} clip={clip} history={history} />;
    } else if (found.track.kind === 'main') {
        body = <ClipPanel comp={comp} clip={found.item as MainClip} history={history} patch={patch} swatches={swatches} />;
    } else if (found.track.kind === 'audio') {
        body = <AudioPanel clip={found.item as AudioClip} patch={patch} />;
    } else {
        const layer = found.item as OverlayLayer;
        body = (
            <>
                {layer.kind === 'text' && <TextPanel comp={comp} layer={layer} patch={patch} swatches={swatches} brand={brand} />}
                {layer.kind === 'shape' && <ShapePanel layer={layer} patch={patch} swatches={swatches} />}
                <LayerPlacement layer={layer} patch={patch} />
                <LayerMotion layer={layer} patch={patch} />
                <Timing comp={comp} item={layer} history={history} patch={patch} />
            </>
        );
    }

    const title = !selection || !found
        ? t('VIDEO_EDITOR.INSPECTOR.VIDEO')
        : selection.kind === 'transition'
        ? t('VIDEO_EDITOR.INSPECTOR.TRANSITION')
        : found.track.kind === 'main'
        ? t(CLIP_TITLES[(found.item as MainClip).kind] ?? CLIP_TITLES.image)
        : found.track.kind === 'audio'
        ? t('VIDEO_EDITOR.INSPECTOR.AUDIO')
        : t(`VIDEO_EDITOR.LAYER.${(found.item as OverlayLayer).kind.toUpperCase()}`);

    return (
        <aside className="flex h-full min-h-0 w-[300px] shrink-0 flex-col border-s bg-background" data-ve-inspector>
            <div className="flex h-10 shrink-0 items-center border-b px-4 text-sm font-medium text-foreground">{title}</div>
            <div className="min-h-0 flex-1 overflow-y-auto">{body}</div>
        </aside>
    );
};

// ─── panels ───────────────────────────────────────────────────────────

type Patch = <T>(id: string, p: Partial<T>, merge?: string) => void;

const CompositionPanel: React.FC<{ comp: VideoComposition; history: CompositionHistory; swatches: string[] }> = ({ comp, history, swatches }) => {
    const { t } = useTranslation();
    return (
        <>
            <PanelSection title={t('VIDEO_EDITOR.INSPECTOR.BACKGROUND')}>
                <ColorRow value={comp.background} swatches={swatches} onChange={(c) => history.commit({ ...comp, background: c }, 'bg')} />
            </PanelSection>
            <PanelSection title={t('VIDEO_EDITOR.INSPECTOR.FRAME_RATE')}>
                <SegmentedChoice
                    value={comp.fps}
                    options={[24, 25, 30].map((f) => ({ value: f, label: `${f} fps` }))}
                    onChange={(f) => history.commit({ ...comp, fps: f })}
                />
            </PanelSection>
            <PanelSection title={t('VIDEO_EDITOR.INSPECTOR.SHORTCUTS')}>
                <ul className="flex flex-col gap-1.5 text-xs text-muted-foreground">
                    {[
                        ['Space', t('VIDEO_EDITOR.SHORTCUT.PLAY')],
                        ['S', t('VIDEO_EDITOR.SHORTCUT.SPLIT')],
                        ['⌫', t('VIDEO_EDITOR.SHORTCUT.DELETE')],
                        ['⌘D', t('VIDEO_EDITOR.SHORTCUT.DUPLICATE')],
                        ['← →', t('VIDEO_EDITOR.SHORTCUT.FRAME')],
                        ['⇧ ← →', t('VIDEO_EDITOR.SHORTCUT.SECOND')],
                        ['⌘Z / ⇧⌘Z', t('VIDEO_EDITOR.SHORTCUT.UNDO')],
                    ].map(([k, label]) => (
                        <li key={k} className="flex items-center justify-between">
                            <span>{label}</span>
                            <kbd className="rounded border bg-muted px-1.5 py-px font-sans text-[10px] text-foreground">{k}</kbd>
                        </li>
                    ))}
                </ul>
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <MousePointerClick className="size-3.5" />
                    {t('VIDEO_EDITOR.INSPECTOR.SELECT_HINT')}
                </p>
            </PanelSection>
        </>
    );
};

const TransitionPicker: React.FC<{ value: TransitionType | undefined; onChange: (v: TransitionType | undefined) => void; allowNone: boolean }> = ({ value, onChange, allowNone }) => {
    const { t } = useTranslation();
    return (
        <div className="grid grid-cols-3 gap-1.5">
            {allowNone && (
                <button
                    type="button"
                    onClick={() => onChange(undefined)}
                    aria-pressed={!value}
                    className={cn(
                        'flex flex-col items-center gap-1.5 rounded-lg border p-1.5 text-[11px] transition-colors hover:bg-muted',
                        !value ? 'border-foreground text-foreground outline outline-[0.5px] outline-foreground' : 'border-border text-muted-foreground',
                    )}
                >
                    <span className="flex h-[44px] w-full items-center justify-center rounded-md bg-muted"><Ban className="size-4" /></span>
                    {t('VIDEO_EDITOR.TRANSITION.NONE')}
                </button>
            )}
            {TRANSITION_TYPES.map((type) => (
                <TransitionTile
                    key={type}
                    type={type}
                    label={t(`VIDEO_EDITOR.TRANSITION.${type.replace('-', '_').toUpperCase()}`)}
                    active={value === type}
                    onClick={() => onChange(type)}
                />
            ))}
        </div>
    );
};

const TransitionPanel: React.FC<{ comp: VideoComposition; clip: MainClip; history: CompositionHistory }> = ({ comp, clip, history }) => {
    const { t } = useTranslation();
    const clips = mainTrack(comp).clips;
    const idx = clips.findIndex((c) => c.id === clip.id);
    const max = Math.max(0.1, maxTransitionDuration(idx > 0 ? clips[idx - 1] : undefined, clip));
    return (
        <PanelSection title={t('VIDEO_EDITOR.INSPECTOR.TRANSITION_IN')}>
            <TransitionPicker
                value={clip.transitionIn?.type}
                allowNone
                onChange={(type) => history.commit(updateItem<MainClip>(comp, clip.id, {
                    // Each type starts at the length it reads best at (a flash is a blink, an iris takes its time).
                    transitionIn: type ? { type, duration: Math.min(TRANSITION_DEFAULT_SECONDS[type], max) } : undefined,
                }))}
            />
            {clip.transitionIn && (
                <SliderRow
                    label={t('VIDEO_EDITOR.INSPECTOR.DURATION')}
                    value={Math.min(clip.transitionIn.duration, max)}
                    min={0.1}
                    max={max}
                    step={0.05}
                    format={secs}
                    onChange={(v) => history.commit(updateItem<MainClip>(comp, clip.id, { transitionIn: { ...clip.transitionIn!, duration: v } }), `${clip.id}:trdur`)}
                />
            )}
        </PanelSection>
    );
};

const ClipPanel: React.FC<{ comp: VideoComposition; clip: MainClip; history: CompositionHistory; patch: Patch; swatches: string[] }> = ({
    comp,
    clip,
    history,
    patch,
    swatches,
}) => {
    const { t } = useTranslation();
    const motion = clip.motion ?? { type: 'none' as MotionType, intensity: 0.4 };
    if (clip.kind === 'color') {
        return (
            <>
                <PanelSection title={t('VIDEO_EDITOR.INSPECTOR.CLIP')}>
                    <ColorRow
                        label={t('VIDEO_EDITOR.INSPECTOR.CARD_COLOR')}
                        value={clip.color || '#000000'}
                        swatches={swatches}
                        onChange={(c) => patch<MainClip>(clip.id, { color: c }, 'card')}
                    />
                    <NumberField
                        label={t('VIDEO_EDITOR.INSPECTOR.DURATION')}
                        value={clip.duration}
                        min={0.2}
                        suffix="s"
                        onChange={(v) => history.commit(setItemDuration(comp, clip.id, v), `${clip.id}:dur`)}
                    />
                </PanelSection>
                <TransitionPanel comp={comp} clip={clip} history={history} />
            </>
        );
    }
    return (
        <>
            <PanelSection title={t('VIDEO_EDITOR.INSPECTOR.CLIP')}>
                <div className="grid grid-cols-2 gap-2">
                    <NumberField
                        label={t('VIDEO_EDITOR.INSPECTOR.DURATION')}
                        value={clip.duration}
                        min={0.2}
                        suffix="s"
                        onChange={(v) => history.commit(setItemDuration(comp, clip.id, v), `${clip.id}:dur`)}
                    />
                    {clip.kind === 'video' && (
                        <NumberField
                            label={t('VIDEO_EDITOR.INSPECTOR.TRIM_IN')}
                            value={clip.trimIn}
                            suffix="s"
                            onChange={(v) => {
                                // The clip eats duration × speed seconds of source.
                                const maxIn = clip.sourceDuration ? Math.max(0, clip.sourceDuration - sourceSpan(clip)) : v;
                                patch<MainClip>(clip.id, { trimIn: Math.min(v, maxIn) }, 'trim');
                            }}
                        />
                    )}
                </div>
                {clip.kind === 'video' && (
                    <SliderRow
                        label={t('VIDEO_EDITOR.INSPECTOR.SPEED')}
                        value={clip.speed ?? 1}
                        min={MIN_SPEED}
                        max={MAX_SPEED}
                        step={0.05}
                        format={(v) => `${Math.round(v * 100) / 100}×`}
                        onChange={(v) => history.commit(setClipSpeed(comp, clip.id, v), `${clip.id}:speed`)}
                    />
                )}
                <SegmentedChoice<ClipFit>
                    value={clip.fit}
                    options={[
                        { value: 'cover', label: t('VIDEO_EDITOR.INSPECTOR.FIT_COVER') },
                        { value: 'contain', label: t('VIDEO_EDITOR.INSPECTOR.FIT_CONTAIN') },
                    ]}
                    onChange={(fit) => patch<MainClip>(clip.id, { fit })}
                />
            </PanelSection>
            <PanelSection title={t('VIDEO_EDITOR.INSPECTOR.MOTION')}>
                <TileGrid<MotionType>
                    value={motion.type}
                    options={MOTION_TYPES.map((m) => ({ value: m, label: t(`VIDEO_EDITOR.MOTION.${m.replace('-', '_').toUpperCase()}`), icon: MOTION_ICONS[m] }))}
                    onChange={(type) => patch<MainClip>(clip.id, { motion: { type, intensity: motion.intensity || 0.4 } })}
                />
                {motion.type !== 'none' && (
                    <SliderRow
                        label={t('VIDEO_EDITOR.INSPECTOR.INTENSITY')}
                        value={motion.intensity}
                        min={0}
                        max={1}
                        format={pct}
                        onChange={(v) => patch<MainClip>(clip.id, { motion: { ...motion, intensity: v } }, 'motion')}
                    />
                )}
            </PanelSection>
            {clip.kind === 'video' && (
                <PanelSection title={t('VIDEO_EDITOR.INSPECTOR.SOUND')}>
                    <label className="flex items-center justify-between text-xs text-muted-foreground">
                        {t('VIDEO_EDITOR.INSPECTOR.MUTE')}
                        <Switch checked={clip.muted} onCheckedChange={(v: boolean) => patch<MainClip>(clip.id, { muted: v })} />
                    </label>
                    <SliderRow
                        label={t('VIDEO_EDITOR.INSPECTOR.VOLUME')}
                        value={clip.volume}
                        min={0}
                        max={1}
                        format={pct}
                        disabled={clip.muted}
                        onChange={(v) => patch<MainClip>(clip.id, { volume: v }, 'vol')}
                    />
                </PanelSection>
            )}
            <TransitionPanel comp={comp} clip={clip} history={history} />
        </>
    );
};

const AudioPanel: React.FC<{ clip: AudioClip; patch: Patch }> = ({ clip, patch }) => {
    const { t } = useTranslation();
    return (
        <PanelSection title={clip.name || t('VIDEO_EDITOR.LAYER.AUDIO')}>
            <SliderRow label={t('VIDEO_EDITOR.INSPECTOR.VOLUME')} value={clip.volume} min={0} max={1} format={pct} onChange={(v) => patch<AudioClip>(clip.id, { volume: v }, 'vol')} />
            <SliderRow label={t('VIDEO_EDITOR.INSPECTOR.FADE_IN')} value={clip.fadeIn} min={0} max={Math.min(6, clip.duration / 2)} step={0.1} format={secs} onChange={(v) => patch<AudioClip>(clip.id, { fadeIn: v }, 'fi')} />
            <SliderRow label={t('VIDEO_EDITOR.INSPECTOR.FADE_OUT')} value={clip.fadeOut} min={0} max={Math.min(6, clip.duration / 2)} step={0.1} format={secs} onChange={(v) => patch<AudioClip>(clip.id, { fadeOut: v }, 'fo')} />
            <div className="grid grid-cols-3 gap-2">
                <NumberField label={t('VIDEO_EDITOR.INSPECTOR.START')} value={clip.start} suffix="s" onChange={(v) => patch<AudioClip>(clip.id, { start: v }, 'start')} />
                <NumberField label={t('VIDEO_EDITOR.INSPECTOR.DURATION')} value={clip.duration} min={0.2} suffix="s" onChange={(v) => patch<AudioClip>(clip.id, { duration: clip.sourceDuration ? Math.min(v, clip.sourceDuration - clip.trimIn) : v }, 'dur')} />
                <NumberField label={t('VIDEO_EDITOR.INSPECTOR.TRIM_IN')} value={clip.trimIn} suffix="s" onChange={(v) => patch<AudioClip>(clip.id, { trimIn: v }, 'trim')} />
            </div>
        </PanelSection>
    );
};

const TextPanel: React.FC<{ comp: VideoComposition; layer: TextLayer; patch: Patch; swatches: string[]; brand: VideoBrand }> = ({
    comp,
    layer,
    patch,
    swatches,
    brand,
}) => {
    const { t } = useTranslation();
    const fonts = [...VIDEO_FONTS.map((f) => f.family)];
    if (brand.fontFamily && !fonts.includes(brand.fontFamily)) fonts.unshift(brand.fontFamily);
    if (!fonts.includes(layer.fontFamily)) fonts.unshift(layer.fontFamily);
    const known = VIDEO_FONTS.find((f) => f.family === layer.fontFamily);
    const weights = known?.weights ?? [300, 400, 500, 600, 700];

    const applyPreset = (id: TextPresetId) => {
        const [styled] = buildPresetLayers(id, { aspect: comp.aspect, start: layer.start, duration: layer.duration, brand, texts: { primary: layer.text } })
            .filter((l) => l.kind === 'text') as TextLayer[];
        if (!styled) return;
        const { id: _id, start, duration, x, y, text, kind, ...style } = styled;
        void _id; void start; void duration; void x; void y; void text; void kind;
        patch<TextLayer>(layer.id, style);
    };

    return (
        <>
            <PanelSection title={t('VIDEO_EDITOR.INSPECTOR.TEXT')}>
                <Textarea
                    value={layer.text}
                    onChange={(e) => patch<TextLayer>(layer.id, { text: e.target.value }, 'text')}
                    className="min-h-[72px] text-sm"
                    data-ve-text-input
                />
                <div className="flex flex-wrap gap-1.5">
                    {TEXT_PRESETS.filter((p) => p.id !== 'end-card').map((p) => (
                        <button
                            key={p.id}
                            type="button"
                            onClick={() => applyPreset(p.id)}
                            className={cn(
                                'rounded-md border px-2 py-0.5 text-[11px] transition-colors hover:bg-muted',
                                layer.stylePreset === p.id ? 'border-foreground text-foreground' : 'border-border text-muted-foreground',
                            )}
                        >
                            {t(p.nameKey)}
                        </button>
                    ))}
                </div>
            </PanelSection>
            <PanelSection title={t('VIDEO_EDITOR.INSPECTOR.TYPOGRAPHY')}>
                <NativeSelect
                    className="w-full"
                    value={layer.fontFamily}
                    onChange={(e) => {
                        void requestFontFamily(e.target.value);
                        patch<TextLayer>(layer.id, { fontFamily: e.target.value });
                    }}
                >
                    {fonts.map((f) => <NativeSelectOption key={f} value={f}>{f}</NativeSelectOption>)}
                </NativeSelect>
                <div className="flex items-center gap-1.5">
                    <NativeSelect
                        className="min-w-0 flex-1"
                        size="sm"
                        value={String(layer.fontWeight)}
                        onChange={(e) => patch<TextLayer>(layer.id, { fontWeight: Number(e.target.value) })}
                    >
                        {weights.map((w) => <NativeSelectOption key={w} value={String(w)}>{t(`VIDEO_EDITOR.WEIGHT.W${w}`)}</NativeSelectOption>)}
                    </NativeSelect>
                    <IconToggle active={layer.fontStyle === 'italic'} label={t('VIDEO_EDITOR.INSPECTOR.ITALIC')} onClick={() => patch<TextLayer>(layer.id, { fontStyle: layer.fontStyle === 'italic' ? 'normal' : 'italic' })}>
                        <Italic />
                    </IconToggle>
                    <IconToggle active={layer.uppercase} label={t('VIDEO_EDITOR.INSPECTOR.UPPERCASE')} onClick={() => patch<TextLayer>(layer.id, { uppercase: !layer.uppercase })}>
                        <CaseUpper />
                    </IconToggle>
                </div>
                <div className="flex items-center gap-1.5">
                    {(['left', 'center', 'right'] as const).map((a) => (
                        <IconToggle key={a} active={layer.align === a} label={t(`VIDEO_EDITOR.INSPECTOR.ALIGN_${a.toUpperCase()}`)} onClick={() => patch<TextLayer>(layer.id, { align: a })}>
                            {a === 'left' ? <AlignLeft /> : a === 'center' ? <AlignCenter /> : <AlignRight />}
                        </IconToggle>
                    ))}
                </div>
                <SliderRow
                    label={t('VIDEO_EDITOR.INSPECTOR.SIZE')}
                    value={layer.fontSize}
                    min={0.008}
                    max={0.16}
                    step={0.001}
                    format={(v) => `${Math.round(v * comp.height)} px`}
                    onChange={(v) => patch<TextLayer>(layer.id, { fontSize: v }, 'size')}
                />
                <SliderRow
                    label={t('VIDEO_EDITOR.INSPECTOR.LETTER_SPACING')}
                    value={layer.letterSpacing}
                    min={-0.08}
                    max={0.6}
                    step={0.005}
                    format={(v) => v.toFixed(2)}
                    onChange={(v) => patch<TextLayer>(layer.id, { letterSpacing: v }, 'ls')}
                />
                <SliderRow
                    label={t('VIDEO_EDITOR.INSPECTOR.LINE_HEIGHT')}
                    value={layer.lineHeight}
                    min={0.8}
                    max={2}
                    step={0.01}
                    format={(v) => v.toFixed(2)}
                    onChange={(v) => patch<TextLayer>(layer.id, { lineHeight: v }, 'lh')}
                />
                <ColorRow label={t('VIDEO_EDITOR.INSPECTOR.COLOR')} value={layer.color} swatches={swatches} onChange={(c) => patch<TextLayer>(layer.id, { color: c }, 'color')} />
            </PanelSection>
            <TextOutline layer={layer} patch={patch} swatches={swatches} />
            <PanelSection title={t('VIDEO_EDITOR.INSPECTOR.TEXT_BACKGROUND')}>
                <SegmentedChoice<'none' | 'pill' | 'box'>
                    value={layer.background?.style ?? 'none'}
                    options={[
                        { value: 'none', label: t('VIDEO_EDITOR.INSPECTOR.BG_NONE') },
                        { value: 'pill', label: t('VIDEO_EDITOR.INSPECTOR.BG_PILL') },
                        { value: 'box', label: t('VIDEO_EDITOR.INSPECTOR.BG_BOX') },
                    ]}
                    onChange={(style) => patch<TextLayer>(layer.id, {
                        background: style === 'none'
                            ? undefined
                            : { color: layer.background?.color ?? '#141412', opacity: layer.background?.opacity ?? 0.6, paddingX: style === 'pill' ? 1 : 0.55, paddingY: style === 'pill' ? 0.55 : 0.35, style },
                    })}
                />
                {layer.background && (
                    <>
                        <ColorRow value={layer.background.color} swatches={swatches} onChange={(c) => patch<TextLayer>(layer.id, { background: { ...layer.background!, color: c } }, 'bgc')} />
                        <SliderRow label={t('VIDEO_EDITOR.INSPECTOR.OPACITY')} value={layer.background.opacity} min={0} max={1} format={pct} onChange={(v) => patch<TextLayer>(layer.id, { background: { ...layer.background!, opacity: v } }, 'bgo')} />
                    </>
                )}
                <label className="flex items-center justify-between text-xs text-muted-foreground">
                    {t('VIDEO_EDITOR.INSPECTOR.SHADOW')}
                    <Switch
                        checked={!!layer.shadow && layer.shadow.opacity > 0}
                        onCheckedChange={(v: boolean) => patch<TextLayer>(layer.id, { shadow: v ? { color: '#000000', opacity: 0.4, blur: 0.35, offsetY: 0.04 } : undefined })}
                    />
                </label>
            </PanelSection>
        </>
    );
};

/** Outline and fill: an outline with the fill off is the big hollow spec caption. */
const TextOutline: React.FC<{ layer: TextLayer; patch: Patch; swatches: string[] }> = ({ layer, patch, swatches }) => {
    const { t } = useTranslation();
    const stroke = layer.stroke && layer.stroke.width > 0 ? layer.stroke : null;
    const filled = (layer.fillOpacity ?? 1) > 0;
    return (
        <PanelSection title={t('VIDEO_EDITOR.INSPECTOR.OUTLINE_SECTION')}>
            <label className="flex items-center justify-between text-xs text-muted-foreground">
                {t('VIDEO_EDITOR.INSPECTOR.OUTLINE')}
                <Switch
                    checked={!!stroke}
                    onCheckedChange={(v: boolean) => patch<TextLayer>(layer.id, v
                        ? { stroke: { color: layer.color || '#FFFFFF', width: 0.03 } }
                        // Hollow type with no outline would vanish: the fill comes back.
                        : { stroke: undefined, fillOpacity: filled ? layer.fillOpacity : 1 })}
                />
            </label>
            <label className="flex items-center justify-between text-xs text-muted-foreground">
                {t('VIDEO_EDITOR.INSPECTOR.FILL')}
                <Switch
                    checked={filled}
                    onCheckedChange={(v: boolean) => patch<TextLayer>(layer.id, v
                        ? { fillOpacity: 1 }
                        : { fillOpacity: 0, stroke: stroke ?? { color: layer.color || '#FFFFFF', width: 0.03 } })}
                />
            </label>
            {stroke && (
                <>
                    <ColorRow value={stroke.color} swatches={swatches} onChange={(c) => patch<TextLayer>(layer.id, { stroke: { ...stroke, color: c } }, 'strokec')} />
                    <SliderRow
                        label={t('VIDEO_EDITOR.INSPECTOR.OUTLINE_WIDTH')}
                        value={stroke.width}
                        min={0.005}
                        max={0.12}
                        step={0.005}
                        format={(v) => `${Math.round(v * 1000) / 10}%`}
                        onChange={(v) => patch<TextLayer>(layer.id, { stroke: { ...stroke, width: v } }, 'strokew')}
                    />
                </>
            )}
        </PanelSection>
    );
};

const ShapePanel: React.FC<{ layer: ShapeLayer; patch: Patch; swatches: string[] }> = ({ layer, patch, swatches }) => {
    const { t } = useTranslation();
    return (
        <PanelSection title={t(`VIDEO_EDITOR.SHAPE.${layer.shape.toUpperCase()}`)}>
            <ColorRow label={t('VIDEO_EDITOR.INSPECTOR.COLOR')} value={layer.color} swatches={swatches} onChange={(c) => patch<ShapeLayer>(layer.id, { color: c }, 'color')} />
            <SliderRow label={t('VIDEO_EDITOR.INSPECTOR.HEIGHT')} value={layer.height} min={layer.shape === 'line' ? 0.0005 : 0.01} max={1} step={0.0005} format={pct} onChange={(v) => patch<ShapeLayer>(layer.id, { height: v }, 'h')} />
            {layer.shape === 'rect' && (
                <SliderRow label={t('VIDEO_EDITOR.INSPECTOR.RADIUS')} value={layer.radius ?? 0} min={0} max={0.2} step={0.002} format={pct} onChange={(v) => patch<ShapeLayer>(layer.id, { radius: v }, 'r')} />
            )}
            {layer.shape === 'scrim' && (
                <SegmentedChoice<'to-bottom' | 'to-top'>
                    value={layer.direction ?? 'to-bottom'}
                    options={[
                        { value: 'to-bottom', label: t('VIDEO_EDITOR.INSPECTOR.SCRIM_BOTTOM') },
                        { value: 'to-top', label: t('VIDEO_EDITOR.INSPECTOR.SCRIM_TOP') },
                    ]}
                    onChange={(direction) => patch<ShapeLayer>(layer.id, { direction })}
                />
            )}
        </PanelSection>
    );
};

const LayerPlacement: React.FC<{ layer: OverlayLayer; patch: Patch }> = ({ layer, patch }) => {
    const { t } = useTranslation();
    return (
        <PanelSection title={t('VIDEO_EDITOR.INSPECTOR.PLACEMENT')}>
            <div className="grid grid-cols-2 gap-x-3 gap-y-3">
                <SliderRow label={t('VIDEO_EDITOR.INSPECTOR.X')} value={layer.x} min={0} max={1} format={pct} onChange={(v) => patch<OverlayLayer>(layer.id, { x: v }, 'x')} />
                <SliderRow label={t('VIDEO_EDITOR.INSPECTOR.Y')} value={layer.y} min={0} max={1} format={pct} onChange={(v) => patch<OverlayLayer>(layer.id, { y: v }, 'y')} />
                <SliderRow label={t('VIDEO_EDITOR.INSPECTOR.WIDTH')} value={layer.width} min={0.05} max={1} format={pct} onChange={(v) => patch<OverlayLayer>(layer.id, { width: v }, 'w')} />
                <SliderRow label={t('VIDEO_EDITOR.INSPECTOR.OPACITY')} value={layer.opacity} min={0} max={1} format={pct} onChange={(v) => patch<OverlayLayer>(layer.id, { opacity: v }, 'o')} />
            </div>
            <SliderRow label={t('VIDEO_EDITOR.INSPECTOR.ROTATION')} value={layer.rotation || 0} min={-45} max={45} step={0.5} format={(v) => `${v.toFixed(1)}°`} onChange={(v) => patch<OverlayLayer>(layer.id, { rotation: v }, 'rot')} />
        </PanelSection>
    );
};

const LayerMotion: React.FC<{ layer: OverlayLayer; patch: Patch }> = ({ layer, patch }) => {
    const { t } = useTranslation();
    const anims = layer.kind === 'text' ? LAYER_ANIMS : LAYER_ANIMS.filter((a) => !TEXT_ONLY_ANIMS.includes(a));
    const options = anims.map((a) => ({
        value: a,
        label: t(`VIDEO_EDITOR.ANIM.${a.replace(/-/g, '_').toUpperCase()}`),
        icon: ANIM_ICONS[a] ?? <span className="text-[11px] font-medium">{a === 'fade' ? 'Aa' : a === 'blur' ? '◌' : a === 'typewriter' ? 'A_' : a === 'wipe' ? '▍' : 'a·b'}</span>,
    }));
    return (
        <PanelSection title={t('VIDEO_EDITOR.INSPECTOR.ANIMATION')}>
            <span className="text-xs text-muted-foreground">{t('VIDEO_EDITOR.INSPECTOR.ANIM_IN')}</span>
            <TileGrid<LayerAnim> value={layer.animIn} options={options} onChange={(a) => patch<OverlayLayer>(layer.id, { animIn: a })} />
            <span className="text-xs text-muted-foreground">{t('VIDEO_EDITOR.INSPECTOR.ANIM_OUT')}</span>
            <TileGrid<LayerAnim>
                value={layer.animOut}
                options={options.filter((o) => !IN_ONLY_ANIMS.includes(o.value))}
                onChange={(a) => patch<OverlayLayer>(layer.id, { animOut: a })}
            />
            <SliderRow label={t('VIDEO_EDITOR.INSPECTOR.ANIM_DURATION')} value={layer.animDuration} min={0.2} max={2.5} step={0.05} format={secs} onChange={(v) => patch<OverlayLayer>(layer.id, { animDuration: v }, 'ad')} />
        </PanelSection>
    );
};

const Timing: React.FC<{ comp: VideoComposition; item: OverlayLayer | ImageLayer; history: CompositionHistory; patch: Patch }> = ({ comp, item, history, patch }) => {
    const { t } = useTranslation();
    return (
        <PanelSection title={t('VIDEO_EDITOR.INSPECTOR.TIMING')}>
            <div className="grid grid-cols-2 gap-2">
                <NumberField label={t('VIDEO_EDITOR.INSPECTOR.START')} value={item.start} suffix="s" onChange={(v) => patch<OverlayLayer>(item.id, { start: v }, 'start')} />
                <NumberField label={t('VIDEO_EDITOR.INSPECTOR.DURATION')} value={item.duration} min={0.2} suffix="s" onChange={(v) => history.commit(setItemDuration(comp, item.id, v), `${item.id}:dur`)} />
            </div>
        </PanelSection>
    );
};

const IconToggle: React.FC<{ active: boolean; label: string; onClick: () => void; children: React.ReactNode }> = ({ active, label, onClick, children }) => (
    <button
        type="button"
        title={label}
        aria-label={label}
        aria-pressed={active}
        onClick={onClick}
        className={cn(
            'flex size-7 items-center justify-center rounded-md border transition-colors [&_svg]:size-3.5',
            active ? 'border-foreground bg-muted text-foreground' : 'border-transparent text-muted-foreground hover:bg-muted hover:text-foreground',
        )}
    >
        {children}
    </button>
);
